/**
 * GlobalAnalytics — admin Command Centre view: who is accessing HiBFF,
 * from which country/region/city, when (day × hour heatmap), which pages,
 * B2C registrations vs B2B school leads, languages and recent leads.
 */
import React, { useEffect, useState } from 'react';
import { Globe, Users, School, Mail, Clock, FileText, MapPin } from 'lucide-react';
import { apiClient } from '../../api/client';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const Kpi = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: tint }}>
      <Icon size={18} className="text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value ?? 0}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  </div>
);

export default function GlobalAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState('');

  useEffect(() => {
    apiClient.request(`/api/analytics/overview?days=${days}`)
      .then(setData)
      .catch(e => setErr(e.message || 'Failed to load analytics'));
  }, [days]);

  if (err) return <div className="p-8 text-red-600" data-testid="global-analytics-error">{err}</div>;
  if (!data) return <div className="p-8 text-gray-500">Loading global analytics…</div>;

  const t = data.totals || {};
  const heatMax = Math.max(1, ...(data.heatmap || []).map(h => h.n));
  const heatMap = {};
  (data.heatmap || []).forEach(h => { heatMap[`${h.weekday}-${h.hour}`] = h.n; });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="global-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Globe size={26} className="text-purple-600" /> Global Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Who is accessing HiBFF, from where, and when — last {data.window_days} days</p>
        </div>
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm" data-testid="analytics-days-select">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={FileText} label="Page views" value={t.pageviews} tint="#7B4FD8" />
        <Kpi icon={Users} label="Sessions" value={t.sessions} tint="#F5831F" />
        <Kpi icon={Users} label="Registrations (B2C)" value={t.registrations} tint="#2EAA5C" />
        <Kpi icon={School} label="School leads (B2B)" value={t.school_leads} tint="#2563EB" />
        <Kpi icon={Globe} label="Countries" value={t.countries} tint="#DB2777" />
        <Kpi icon={Mail} label="Emails sent (all time)" value={data.emails_sent_total} tint="#0891B2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="analytics-by-country">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Globe size={16} /> By country</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-400 uppercase"><th className="pb-2">Country</th><th>Visits</th><th>Sessions</th><th>B2C reg</th><th>B2B leads</th></tr></thead>
            <tbody>
              {(data.by_country || []).slice(0, 12).map(c => (
                <tr key={c._id} className="border-t border-gray-100">
                  <td className="py-1.5 font-medium">{c._id || 'Unknown'}</td>
                  <td>{c.visits}</td><td>{c.sessions}</td>
                  <td className="text-green-700 font-semibold">{c.registrations}</td>
                  <td className="text-blue-700 font-semibold">{c.b2b}</td>
                </tr>
              ))}
              {!data.by_country?.length && <tr><td colSpan={5} className="py-4 text-gray-400 text-center">No data yet — traffic appears here as visitors arrive</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="analytics-by-city">
          <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><MapPin size={16} /> Top regions / cities / towns</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-400 uppercase"><th className="pb-2">City / Town</th><th>Region</th><th>Country</th><th>Visits</th></tr></thead>
            <tbody>
              {(data.by_city || []).slice(0, 12).map((c, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="py-1.5 font-medium">{c.city}</td><td>{c.region}</td><td>{c.country}</td><td>{c.visits}</td>
                </tr>
              ))}
              {!data.by_city?.length && <tr><td colSpan={4} className="py-4 text-gray-400 text-center">No city data yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="analytics-heatmap">
        <h2 className="font-bold text-gray-900 mb-3 flex items-center gap-2"><Clock size={16} /> Time-of-day access heatmap (UTC)</h2>
        <div className="overflow-x-auto">
          <div className="grid gap-0.5 min-w-[720px]" style={{ gridTemplateColumns: '44px repeat(24, 1fr)' }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => <div key={h} className="text-[9px] text-gray-400 text-center">{h}</div>)}
            {DAYS.map((d, wd) => (
              <React.Fragment key={d}>
                <div className="text-[10px] text-gray-500 pr-1 flex items-center">{d}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const n = heatMap[`${wd}-${h}`] || 0;
                  const alpha = n ? 0.15 + 0.85 * (n / heatMax) : 0;
                  return <div key={h} title={`${d} ${h}:00 — ${n} views`} className="h-5 rounded-sm" style={{ background: n ? `rgba(123,79,216,${alpha})` : '#F3F4F6' }} />;
                })}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="analytics-top-pages">
          <h2 className="font-bold text-gray-900 mb-3">Pages being opened</h2>
          {(data.top_pages || []).slice(0, 12).map(p => (
            <div key={p._id} className="flex items-center justify-between py-1.5 border-t border-gray-100 text-sm">
              <span className="font-mono text-xs text-gray-700 truncate max-w-[60%]">{p._id}</span>
              <span className="text-gray-500">{p.views} views · {p.sessions} sessions</span>
            </div>
          ))}
          {!data.top_pages?.length && <p className="py-4 text-gray-400 text-sm text-center">No pageviews yet</p>}
          <div className="mt-3 text-xs text-gray-400 flex gap-2 flex-wrap">
            {(data.by_language || []).map(l => <span key={l._id} className="px-2 py-0.5 bg-gray-100 rounded-full">{l._id}: {l.views}</span>)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="analytics-recent-leads">
          <h2 className="font-bold text-gray-900 mb-3">Recent conversions & leads (with location)</h2>
          {(data.recent_conversions || []).slice(0, 10).map((r, i) => (
            <div key={i} className="py-1.5 border-t border-gray-100 text-sm flex items-center justify-between">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.event_type === 'registration' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {r.event_type === 'registration' ? `B2C ${r.persona || ''}` : 'B2B school'}
              </span>
              <span className="text-gray-600 text-xs">{[r.city, r.region, r.country].filter(Boolean).join(', ') || 'Unknown'} · {r.lang}</span>
              <span className="text-gray-400 text-xs">{(r.ts || '').slice(0, 16).replace('T', ' ')}</span>
            </div>
          ))}
          {!data.recent_conversions?.length && <p className="py-4 text-gray-400 text-sm text-center">Conversions appear here as they happen</p>}
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Click-level heatmaps (mouse movement / scroll maps) need a session-replay tool — Microsoft Clarity is free and can be added with one script tag. Ask me to wire it when ready.
      </p>
    </div>
  );
}
